import styled from '@emotion/styled';
import clsx from 'clsx';
import dynamic from 'next/dynamic';
import { useRouter } from 'next/navigation';
import React, { useCallback, useEffect, useImperativeHandle, useRef, useState } from 'react';
import { Index } from 'react-instantsearch';
import useCustomer from '~/features/commerce-api/hooks/useCustomer';
import FilterDrawer from '~/features/productList/components/FilterDrawer/FilterDrawer';
import HitsHeader from '~/features/productList/components/HitsHeader/HitsHeader';
import { mq } from '~/lib';
import { Link } from '~/shared/components';
import { Container } from '~/shared/components/Container';
import Flex from '~/shared/components/Flex';
import TopBanner from '~/shared/components/TopBanner/TopBanner';
import { useIsMobile } from '~/shared/hooks/useIsMobile/useIsMobile';
import { useFrame } from '~/shared/utils';
import { normalizeHexColor } from '~/shared/utils/colors';
import { withErrorBoundary } from '~/shared/utils/errorBoundary';
import { useGetTotalHits } from '~/shared/utils/useGetTotalHits';
import { SkipLink } from '~/templates/pages/components/DynamicLayout/components/Standard/styled';
import { usePage } from '~/templates/pages/hooks/usePage';
import { theme } from '~/theme';
import { useBannerState } from '../../hooks/useBannerState';
import MegaMenu from '../N30MegaMenu/MegaMenu';
import { SearchWithChatLauncher } from './components/SearchWithChatLauncher';
import { DesktopMetaMenuItem } from './components/DesktopMetaMenuItem';
import { FavoriteButton } from './components/FavoriteButton';
import { LoginButton } from './components/LoginButton';
import { MiniBasketButton } from './components/MiniBasketButton';
import { MobileMegaMenuButton } from './components/MobileMegaMenuButton';
import {
    MenuHeaderContainer,
    ProgressLoader,
    StyledIconNav,
    StyledLeftContainer,
    StyledLogo,
    StyledLogoContainer,
    StyledRightContainer,
    StyledSearchInputContainer,
    StyledSearchInputContainerMobile,
    SearchInputMobileWrapper,
} from './styled';
import { SearchForm } from '../search/SearchForm';
import {
    VirtualFacets,
    VirtualPagination,
    VirtualSearch,
    VirtualSortBy,
} from '~/features/productList/components/VirtualWidgets';
import { AlgoliaProductList } from '~/services/instantsearch/components';
import { useAlgoliaProductSearch } from '~/services/instantsearch/hooks/useAlgoliaProductSearch';
import {
    ALGOLIA_INDEX_ID_PRODUCT_LIST,
    ALGOLIA_INDEX_MAIN,
} from '~/services/instantsearch/variables';
import { useSearchOverlay } from '~/services/search/useSearchOverlay';
import styles from './N20MainMenu.module.scss';

type NavJustifyContent = {
    center: string;
    end: string;
    start: string;
};

type Props = {
    showTopBanner: boolean;
} & React.HTMLAttributes<HTMLDivElement>;

const SearchOverlay = dynamic(() => import('../search/SearchOverlay'), {
    ssr: false,
});

const N20MainMenuComponent = React.forwardRef<HTMLDivElement, Props>((props, ref) => {
    const { showTopBanner } = props;
    const isMobile = useIsMobile();
    const { totalHits } = useGetTotalHits();
    const router = useRouter();
    const { data: frame } = useFrame();
    const { isStorePersonnel } = useCustomer();
    const page = usePage();
    const { query, isOverlayVisible, setIsOverlayVisible } = useSearchOverlay();
    const { generateSearchPath } = useAlgoliaProductSearch({ query });

    const headerRef = useRef<HTMLDivElement>(null);
    useImperativeHandle(ref, () => headerRef.current as HTMLDivElement, [ref]);
    const justify: NavJustifyContent = {
        center: 'center',
        end: 'flex-end',
        start: 'flex-start',
    };

    // PLP detection used to decide whether the header should behave like
    // the PLP sticky (menu header hidden + filter bar shown).
    // Prefer `page.type` so we don't depend on DOM nodes that might be hidden
    // while scrolling.
    const isPLPPage = page.type === 'p40ProductListPage' || page.type === 'p10SearchPage';

    const searchOverlayRef = useRef<HTMLDivElement>(null);
    const menuHeaderRef = useRef<HTMLDivElement>(null);
    const headerNavigationRef = useRef<HTMLDivElement>(null);
    const searchContainerRef = useRef<HTMLDivElement>(null);
    const topBannerRef = useRef<HTMLDivElement>(null);
    const plpFiltersRef = useRef<HTMLDivElement>(null);

    const { setHeaderHeight } = useBannerState();
    const [hasTopBanner, setHasTopBanner] = useState(false);
    const topBannerHeightRef = useRef<number>(-1);
    const hasTopBannerRef = useRef<boolean>(false);

    const headerMobileSearchWrapperRef = useRef<HTMLDivElement>(null);
    const plpHeaderMobileSearchWrapperRef = useRef<HTMLDivElement>(null);

    // Function to update banner height - more robust calculation
    const updateBannerHeight = (element: HTMLElement, component: HTMLElement) => {
        // Always derive height from what is *actually rendered*.
        // This stays correct when CMS removes `top` and/or `inStoreBanner`,
        // because we no longer rely on DOM order heuristics (nth-child, hardcoded
        // fallback values, etc).
        const rectHeight = element.getBoundingClientRect().height;
        const offsetHeight = element.offsetHeight;

        // Prefer bounding rect height, but fall back to offsetHeight if needed.
        // Use floor to avoid 1px seams caused by rounding up fractional heights.
        const finalHeight = Math.max(
            0,
            Math.floor(Number.isFinite(rectHeight) && rectHeight >= 0 ? rectHeight : offsetHeight),
        );

        // Avoid re-applying CSS vars / state when height hasn't changed.
        // This matters because observers can fire multiple times during async updates.
        if (Math.abs(finalHeight - topBannerHeightRef.current) < 1) return;

        topBannerHeightRef.current = finalHeight;

        // Set the CSS variable on both the component and the header element
        component.style.setProperty('--top-banner-height', `${finalHeight}px`);

        // Also set it on the header element where it's actually used
        const headerElement = component.querySelector('.header') as HTMLElement;
        if (headerElement) {
            headerElement.style.setProperty('--top-banner-height', `${finalHeight}px`);
        }

        const nextHasTopBanner = finalHeight > 0;
        if (hasTopBannerRef.current !== nextHasTopBanner) {
            hasTopBannerRef.current = nextHasTopBanner;
            setHasTopBanner(nextHasTopBanner);
        }
    };

    // Effect to track component heights and update CSS variables
    useEffect(() => {
        if (!isMobile) return;

        const component = headerRef.current;
        const topBanner = topBannerRef.current;
        const menuHeader = menuHeaderRef.current;

        if (!component || !menuHeader || !topBanner) return;

        let rafId: number | null = null;
        let pendingElement: HTMLElement | null = null;
        const scheduleUpdate = (el: HTMLElement) => {
            pendingElement = el;
            if (rafId !== null) return;
            rafId = window.requestAnimationFrame(() => {
                rafId = null;
                if (pendingElement) {
                    updateBannerHeight(pendingElement, component);
                    pendingElement = null;
                }
            });
        };

        // Create ResizeObserver to track size changes
        const resizeObserver = new ResizeObserver((entries) => {
            for (const entry of entries) {
                const element = entry.target;

                if (element === topBanner) {
                    scheduleUpdate(element as HTMLElement);
                }

                if (element === menuHeader) {
                    const height = entry.contentRect.height;
                    component.style.setProperty('--menu-header-height', `${Math.round(height)}px`);
                }
            }
        });

        // Create MutationObserver to track DOM changes in TopBanner
        const mutationObserver = new MutationObserver((mutations) => {
            // Check if any mutation affects the TopBanner structure
            const hasRelevantChanges = mutations.some((mutation) => {
                // Track child additions/removals (like inStoreBanner appearing)
                if (mutation.type === 'childList') {
                    return true;
                }

                // Track attribute changes (like style changes affecting height)
                if (mutation.type === 'attributes') {
                    const target = mutation.target as Element;
                    return (
                        target.classList.contains('banner') ||
                        target.classList.contains('slider') ||
                        target.classList.contains('component')
                    );
                }

                // Track text content changes (banner text updates)
                if (mutation.type === 'characterData') {
                    return true;
                }

                return false;
            });

            if (hasRelevantChanges) {
                // Update height after a small delay to ensure DOM is settled
                requestAnimationFrame(() => {
                    scheduleUpdate(topBanner);
                });
            }
        });

        // Start observing
        resizeObserver.observe(menuHeader);
        resizeObserver.observe(topBanner);

        mutationObserver.observe(topBanner, {
            childList: true, // Track added/removed elements
            subtree: true, // Track changes in descendants
            attributes: true, // Track attribute changes
            characterData: true, // Track text content changes
        });

        // Initial height calculation
        updateBannerHeight(topBanner, component);
        // One-shot follow-up to catch late layout changes without polling.
        window.setTimeout(() => updateBannerHeight(topBanner, component), 350);

        return () => {
            resizeObserver.disconnect();
            mutationObserver.disconnect();
            if (rafId !== null) window.cancelAnimationFrame(rafId);
        };
    }, [isMobile, isStorePersonnel]);

    // Additional effect to handle isStorePersonnel changes
    useEffect(() => {
        if (!isMobile || !headerRef.current || !topBannerRef.current) return;

        // Update height when store personnel status changes (affects inStoreBanner)
        const updateHeight = () => {
            updateBannerHeight(topBannerRef.current!, headerRef.current!);
        };

        // Small delay to allow DOM to update
        const timeoutId = setTimeout(updateHeight, 100);

        return () => clearTimeout(timeoutId);
    }, [isStorePersonnel, isMobile]);

    useEffect(() => {
        const htmlElement = document.documentElement;
        const headerHide = styles.headerHide;
        const headerShow = styles.headerShow;
        const searchHidden = styles.searchHidden;
        let didScroll = false;
        const delta = 30;
        let lastScrollTop = 0;
        const baseStartSticky = document.querySelector('#mainHeader')?.clientHeight || 100;
        // PLP workaround: delay the "header collapsed" state so the sticky
        // filter bar doesn't appear simultaneously with the in-page facets
        // (prevents the "double filter" look).
        const startSticky = isPLPPage ? 800 : baseStartSticky;

        const didScrollFn = () => {
            didScroll = true;
        };

        const hasScrolled = () => {
            // Disable scroll detection when search inputs are focused and SearchOverlay is present
            const activeElement = document.activeElement;
            const isSearchInputFocused =
                activeElement?.matches('input[type="text"], input[type="search"]') ||
                activeElement?.getAttribute('data-testid') === 'search-input' ||
                activeElement?.getAttribute('data-testid') === 'mirror-search-input';

            const isSearchOverlayPresent =
                isOverlayVisible ||
                document.querySelector('[data-testid="search-overlay"]') !== null ||
                document.querySelector('.search-overlay') !== null;

            if (isSearchInputFocused && isSearchOverlayPresent) {
                // Don't modify header classes when search is active
                return;
            }

            const windowScrollTop = window.scrollY;

            if (windowScrollTop > startSticky) {
                htmlElement.classList.add(headerHide);
            } else {
                htmlElement.classList.remove(headerHide);
            }

            // Non-PLP behavior: after the header collapses (topbar hidden),
            // don't re-show it on scroll-up. We only use `headerShow` on PLP
            // to swap the menu header with the filter bar.
            if (!isPLPPage) {
                htmlElement.classList.remove(headerShow);
            }

            if (Math.abs(lastScrollTop - windowScrollTop) <= delta) {
                return;
            }

            // Non-PLP mobile UX: hide the search bar on scroll-down and re-show on scroll-up,
            // while keeping the top banner/menu behavior unchanged.
            if (!isPLPPage) {
                const searchWrapper = headerMobileSearchWrapperRef.current;
                if (searchWrapper) {
                    const isHeaderCollapsed = windowScrollTop > startSticky;
                    const isScrollingDown = windowScrollTop > lastScrollTop;
                    searchWrapper.classList.toggle(
                        searchHidden,
                        isHeaderCollapsed && isScrollingDown,
                    );
                }
            } else {
                const isHeaderCollapsed = windowScrollTop > startSticky;
                const isScrollingDown = windowScrollTop > lastScrollTop;

                // Hide/show the "top header" search on PLP as well (non-filter-container search).
                headerMobileSearchWrapperRef.current?.classList.toggle(
                    searchHidden,
                    // Keep it hidden while header is collapsed, even on scroll-up,
                    // so it doesn't peek while the filter container is appearing.
                    isHeaderCollapsed,
                );

                // Hide/show the PLP filter-bar search on scroll direction.
                plpHeaderMobileSearchWrapperRef.current?.classList.toggle(
                    searchHidden,
                    isHeaderCollapsed && isScrollingDown,
                );
            }

            if (isPLPPage) {
                if (windowScrollTop > lastScrollTop) {
                    htmlElement.classList.remove(headerShow);
                } else if (
                    windowScrollTop + window.outerHeight <
                    document.documentElement.scrollHeight
                ) {
                    // Restore PLP sticky filter container behavior:
                    // add `headerShow` when scrolling up (as long as we're not at
                    // the bottom), and let the separate search wrapper logic
                    // prevent any "peek" of the top search bar.
                    htmlElement.classList.add(headerShow);
                }
            }

            // Update header height for MegaMenu when header state changes
            if (headerRef.current) {
                // Get current header state
                const htmlClasses = document.documentElement.className;
                const hasHeaderHide = htmlClasses.includes('headerHide');
                const hasHeaderShow = htmlClasses.includes('headerShow');
                const isHeaderPartiallyHidden = hasHeaderHide && !hasHeaderShow;
                const isHeaderCompletelyHidden = hasHeaderHide && hasHeaderShow;

                let visibleHeight = headerRef.current.offsetHeight;

                if (isMobile) {
                    if (isHeaderPartiallyHidden) {
                        // When partially hidden, only show menu header
                        const menuHeaderHeight = menuHeaderRef.current?.clientHeight || 0;
                        visibleHeight = menuHeaderHeight;
                    } else if (isHeaderCompletelyHidden) {
                        // When completely hidden on PLP pages, use filter container height (120px)
                        if (isPLPPage) {
                            visibleHeight = 120; // Filter container height
                        } else {
                            visibleHeight = 0; // Completely hidden
                        }
                    }
                }

                headerRef.current.style.setProperty(
                    '--mainNavigation-height',
                    `${visibleHeight}px`,
                );
            }

            lastScrollTop = windowScrollTop;
        };

        // Route changes can occur without a subsequent `scroll` event.
        // If the destination page loads at the top, make sure search is visible
        // again (clear stale `searchHidden` state from the previous page).
        const initialScrollTop = window.scrollY;
        lastScrollTop = initialScrollTop;
        const isAtTop = initialScrollTop <= startSticky;
        if (isAtTop) {
            headerMobileSearchWrapperRef.current?.classList.remove(searchHidden);
            plpHeaderMobileSearchWrapperRef.current?.classList.remove(searchHidden);
        }

        const interval = setInterval(() => {
            if (didScroll) {
                hasScrolled();
                didScroll = false;
            }
        }, 50);

        window.addEventListener('scroll', didScrollFn);

        return () => {
            clearInterval(interval);
            window.removeEventListener('scroll', didScrollFn);
        };
    }, [isOverlayVisible, page.type, isPLPPage, isMobile]);

    useEffect(() => {
        if (!headerRef.current) return;

        const updateHeaderHeight = () => {
            const headerHeight = headerRef.current?.offsetHeight || 0;
            setHeaderHeight(headerHeight);

            // Set the mainNavigation-height CSS variable for MegaMenu
            if (headerRef.current) {
                // Get current header state
                const htmlClasses = document.documentElement.className;
                const hasHeaderHide = htmlClasses.includes('headerHide');
                const hasHeaderShow = htmlClasses.includes('headerShow');
                const isHeaderPartiallyHidden = hasHeaderHide && !hasHeaderShow;
                const isHeaderCompletelyHidden = hasHeaderHide && hasHeaderShow;

                let visibleHeight = headerHeight;

                // Mobile-only collapse — see matching comment above. On desktop the
                // mega menu must always sit directly below the (constant-height) header.
                if (isMobile) {
                    if (isHeaderPartiallyHidden) {
                        // When partially hidden, only show menu header
                        const menuHeaderHeight = menuHeaderRef.current?.clientHeight || 0;
                        visibleHeight = menuHeaderHeight;
                    } else if (isHeaderCompletelyHidden) {
                        // When completely hidden on PLP pages, use filter container height (120px)
                        if (isPLPPage) {
                            visibleHeight = 120; // Filter container height
                        } else {
                            visibleHeight = 0; // Completely hidden
                        }
                    }
                }

                headerRef.current.style.setProperty(
                    '--mainNavigation-height',
                    `${visibleHeight}px`,
                );
            }
        };

        // Initial measurement
        updateHeaderHeight();

        // Update on resize
        window.addEventListener('resize', updateHeaderHeight);

        // Update when header classes change (for MegaMenu positioning). Also watch
        const observer = new MutationObserver((mutations) => {
            mutations.forEach((mutation) => {
                if (
                    mutation.type === 'attributes' &&
                    (mutation.attributeName === 'class' ||
                        mutation.attributeName === 'data-menu-open')
                ) {
                    updateHeaderHeight();
                }
            });
        });

        observer.observe(document.documentElement, {
            attributes: true,
            attributeFilter: ['class', 'data-menu-open'],
        });

        // Also catch the header's own size settling after mount (e.g. the top
        // banner finishing layout) without needing a resize/class-change event.
        const headerResizeObserver = new ResizeObserver(() => updateHeaderHeight());
        headerResizeObserver.observe(headerRef.current);

        return () => {
            window.removeEventListener('resize', updateHeaderHeight);
            observer.disconnect();
            headerResizeObserver.disconnect();
        };
    }, [headerRef, setHeaderHeight, isPLPPage, isMobile]);

    const onSearchSubmit = useCallback(
        (value: string) => {
            const path = generateSearchPath(value);

            setIsOverlayVisible(false);
            router.push(path);
        },
        [generateSearchPath],
    );

    const isListPage = page.type === 'p40ProductListPage' || page.type === 'p10SearchPage';

    return (
        <>
            <header
                className={styles.component}
                tabIndex={-1}
                key="mainMenu"
                ref={headerRef}
                id="mainNavigation"
            >
                <div
                    className={clsx(styles.header, {
                        'has-top-banner': hasTopBanner,
                    })}
                    id="mainHeader"
                >
                    <SkipLink
                        href="#mainContent"
                        onClick={(e) => {
                            e.preventDefault();
                            router.replace(e.currentTarget.href);
                            e.currentTarget.blur();
                        }}
                    >
                        Gå til hovedindhold
                    </SkipLink>

                    <Container
                        className={clsx(styles.container, {
                            [styles.isListPage]: isListPage,
                        })}
                        fullWidth
                    >
                        <div ref={topBannerRef}>
                            <TopBanner
                                inStoreBanner={
                                    frame.header?.inStoreBanner && isStorePersonnel
                                        ? {
                                              color: normalizeHexColor(
                                                  frame.header?.inStoreTextColor,
                                              ),
                                              background: normalizeHexColor(
                                                  frame.header?.inStoreBackgroundColor,
                                              ),
                                              text: frame.header?.inStoreBanner ?? '',
                                          }
                                        : undefined
                                }
                                top={
                                    showTopBanner
                                        ? {
                                              color: normalizeHexColor(
                                                  frame.header?.topBannerTextColor,
                                              ),
                                              background: normalizeHexColor(
                                                  frame.header?.topBannerBackGroundColor,
                                              ),
                                              text: frame.header?.topBanner ?? '',
                                          }
                                        : undefined
                                }
                                campaign={
                                    frame.header?.campaignBannerLink
                                        ? {
                                              color: normalizeHexColor(frame.header?.textColor),
                                              background: normalizeHexColor(
                                                  frame.header?.backGroundColor,
                                              ),
                                              text: frame.header?.campaignBannerText ?? '',
                                              link: frame.header?.campaignBannerLink,
                                          }
                                        : undefined
                                }
                                uspLinks={
                                    frame.navigation?.metaMenu?.UspLinks?.length
                                        ? {
                                              color: normalizeHexColor(frame.header?.uspTextColor),
                                              background: normalizeHexColor(
                                                  frame.header?.uspBackgroundColor,
                                              ),
                                              usp: frame.navigation?.metaMenu?.UspLinks ?? [],
                                          }
                                        : undefined
                                }
                            />
                        </div>

                        <MenuHeaderContainer ref={menuHeaderRef}>
                            <StyledLeftContainer>
                                <StyledIconNav mobile justifyContent={justify.start}>
                                    <MobileMegaMenuButton />
                                    <LoginButton textColor={theme.colors.black} desktop metaMenu />
                                </StyledIconNav>

                                {!isMobile && (
                                    <StyledIconNav
                                        notMobile={true}
                                        justifyContent={'flex-start'}
                                        tabIndex={-1}
                                    >
                                        <SearchWithChatLauncher>
                                            <StyledSearchInputContainer
                                                ref={searchContainerRef}
                                                activeOverlay={isOverlayVisible}
                                                notMobile={true}
                                                data-testid="search-input-container"
                                                tabIndex={-1}
                                            >
                                                <SearchForm onSubmit={onSearchSubmit} />

                                                <SearchOverlay
                                                    topBannerRef={topBannerRef}
                                                    variant="search"
                                                    parentElement={searchOverlayRef}
                                                    headerRef={headerRef}
                                                    menuHeaderRef={menuHeaderRef}
                                                    headerNavigationRef={headerNavigationRef}
                                                />
                                            </StyledSearchInputContainer>
                                        </SearchWithChatLauncher>
                                    </StyledIconNav>
                                )}
                            </StyledLeftContainer>
                            {!isMobile && <div ref={searchOverlayRef} />}
                            <StyledLogoContainer
                                onFocus={() => setIsOverlayVisible(false)}
                                activeOverlay={isOverlayVisible}
                            >
                                <Link aria-label="Imerco" href="/">
                                    <StyledLogo />
                                </Link>
                            </StyledLogoContainer>

                            <StyledRightContainer>
                                <StyledIconNav
                                    data-testid="top-icon-nav"
                                    justifyContent={justify.end}
                                >
                                    <Flex
                                        css={{
                                            [mq(0, 'md')]: {
                                                display: 'none',
                                            },
                                            gap: '16px',
                                        }}
                                    >
                                        {frame.navigation?.metaMenu?.DesktopLinks?.map(
                                            (section) => {
                                                return (
                                                    <DesktopMetaMenuItem
                                                        key={section.id}
                                                        section={section}
                                                        textColor={theme.colors.black}
                                                        desktop
                                                        metaMenu
                                                    />
                                                );
                                            },
                                        )}
                                    </Flex>
                                    <LoginButtonT textColor={theme.colors.black} desktop metaMenu />

                                    <FavoriteButton
                                        textColor={theme.colors.black}
                                        desktop
                                        metaMenu
                                    />
                                    <MiniBasketButton
                                        textColor={theme.colors.black}
                                        desktop
                                        metaMenu
                                    />
                                </StyledIconNav>
                            </StyledRightContainer>
                        </MenuHeaderContainer>

                        {isMobile && (
                            <SearchInputMobileWrapper
                                ref={headerMobileSearchWrapperRef}
                                className={styles.searchWrapper}
                            >
                                <SearchWithChatLauncher>
                                    <StyledSearchInputContainerMobile
                                        ref={searchContainerRef}
                                        notMobile={false}
                                        activeOverlay={isOverlayVisible}
                                        data-testid="search-input-container"
                                    >
                                        <SearchForm onSubmit={onSearchSubmit} />

                                        <SearchOverlay
                                            variant="searchMobile"
                                            topBannerRef={topBannerRef}
                                            overlayTop={50}
                                            parentElement={searchOverlayRef}
                                            headerRef={headerRef}
                                            menuHeaderRef={menuHeaderRef}
                                            headerNavigationRef={headerNavigationRef}
                                        />
                                    </StyledSearchInputContainerMobile>
                                </SearchWithChatLauncher>
                            </SearchInputMobileWrapper>
                        )}
                    </Container>
                </div>

                {isMobile && <div ref={searchOverlayRef} />}

                <div ref={headerNavigationRef}>
                    <MegaMenu navigation={frame.navigation} />
                </div>

                <ProgressLoader id="progress-loader" />
            </header>

            <div className={styles.plpOverlayTarget} ref={plpFiltersRef} />

            {isMobile && isListPage && (
                <Container className={styles.filterContainer}>
                    <SearchInputMobileWrapper
                        ref={plpHeaderMobileSearchWrapperRef}
                        className={styles.searchWrapper}
                    >
                        <SearchWithChatLauncher showHint={false}>
                            <StyledSearchInputContainerMobile
                                notMobile={false}
                                activeOverlay={isOverlayVisible}
                                data-testid="search-input-container"
                            >
                                <SearchForm onSubmit={onSearchSubmit} />

                                <SearchOverlay
                                    topBannerRef={topBannerRef}
                                    variant="searchMobile"
                                    overlayTop={50}
                                    parentElement={plpFiltersRef}
                                    headerRef={headerRef}
                                    menuHeaderRef={menuHeaderRef}
                                    headerNavigationRef={headerNavigationRef}
                                />
                            </StyledSearchInputContainerMobile>
                        </SearchWithChatLauncher>
                    </SearchInputMobileWrapper>

                    <AlgoliaProductList>
                        <Container className={styles.productListToolbar} hasGutter>
                            <Index
                                indexName={ALGOLIA_INDEX_MAIN}
                                indexId={ALGOLIA_INDEX_ID_PRODUCT_LIST}
                            >
                                <VirtualFacets />
                                <VirtualSearch />
                                <VirtualSortBy />
                                <VirtualPagination />

                                <FilterDrawer showDrawer={false} />

                                <HitsHeader
                                    className={styles.productListHits}
                                    totalHits={totalHits}
                                />
                            </Index>
                        </Container>
                    </AlgoliaProductList>
                </Container>
            )}
        </>
    );
});

N20MainMenuComponent.displayName = 'N20MainMenuComponent';

const LoginButtonT = styled(LoginButton)(() => ({
    [mq(0, 'md')]: {
        display: 'none',
    },
}));

export const N20MainMenu = withErrorBoundary(N20MainMenuComponent);
